import React, { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import type { CreateVendedorRequest, Genero, VendedorDTO, WrapperResponse } from '../../../model/kartera-api-model'
import { useHistory } from '../../../router/history'
import APIInvoke from '../../../utils/APIInvoke'
import { clip, onlyLetters, onlySimple } from '../../../utils/inputs'
import { errorMessage, SELLERS_PATH } from '../common/seller-common'

export interface SellerFormState {
    id: number | null
    nombres: string
    apPat: string
    apMat: string
    sexo: Genero | ''
    rfc: string
    correo: string
    telefono: string
}

export interface SellerFormProps {
    modal?: boolean
    vendedor?: Partial<VendedorDTO>
    onCompleted?: (vendedor?: VendedorDTO | null) => void
}

export interface FormMessage {
    type: 'error' | 'success'
    message: string
}

const LOAD_ERROR = 'No se pudo cargar el vendedor.'
const SAVE_ERROR = 'Error al guardar el vendedor'

const emptyState: SellerFormState = {
    id: null,
    nombres: '',
    apPat: '',
    apMat: '',
    sexo: '',
    rfc: '',
    correo: '',
    telefono: '',
}

const toFormState = (body: Partial<VendedorDTO>): SellerFormState => ({
    ...emptyState,
    id: body.id ?? null,
    nombres: body.nombres || '',
    apPat: body.apPat || '',
    apMat: body.apMat || '',
    sexo: body.sexo || '',
    rfc: body.rfc || '',
    correo: body.correo || '',
    telefono: body.telefono || '',
})

const toRequest = (state: SellerFormState, sexo: Genero): CreateVendedorRequest => ({
    id: state.id,
    nombres: state.nombres,
    apPat: state.apPat,
    apMat: state.apMat,
    sexo,
    rfc: state.rfc,
    correo: state.correo,
    telefono: state.telefono,
})

export const useSellerFormModel = ({ modal, vendedor, onCompleted }: SellerFormProps) => {
    const history = useHistory()
    const location = useLocation()
    const { vendedorId } = useParams()
    const backUrl: string = (location.state as { backUrl?: string } | null)?.backUrl || SELLERS_PATH

    const [loading, setLoading] = useState(false)
    const [message, setMessage] = useState<FormMessage | null>(null)
    const [state, setState] = useState<SellerFormState>(emptyState)

    useEffect(() => {
        const parametroVendedorId = vendedorId || vendedor?.id
        if (!parametroVendedorId) {
            if (vendedor) setState(toFormState(vendedor))
            return undefined
        }
        let active = true
        const load = async () => {
            try {
                setLoading(true)
                const response: WrapperResponse<VendedorDTO> | null = await (await APIInvoke.invokeGET(`/vendedores/${parametroVendedorId}`)).json()
                if (!active) return
                if (!response?.ok || !response.body) throw new Error(response?.message || LOAD_ERROR)
                setState(toFormState(response.body))
            } catch (error) {
                console.error(error)
                if (active) setMessage({ type: 'error', message: errorMessage(error, LOAD_ERROR) })
            } finally {
                if (active) setLoading(false)
            }
        }
        load()
        return () => { active = false }
    }, [vendedorId, vendedor])

    const patch = (partial: Partial<SellerFormState>) => setState((current) => ({ ...current, ...partial }))

    const change = {
        nombres: (value: string) => patch({ nombres: onlyLetters(value, 200) }),
        apPat: (value: string) => patch({ apPat: onlyLetters(value, 60) }),
        apMat: (value: string) => patch({ apMat: onlyLetters(value, 60) }),
        sexo: (value: Genero | '') => patch({ sexo: value }),
        rfc: (value: string) => patch({ rfc: onlySimple(value, 13).toUpperCase() }),
        correo: (value: string) => patch({ correo: clip(value, 100) }),
        telefono: (value: string) => patch({ telefono: value }),
    }

    const save = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        if (!state.sexo) {
            setMessage({ type: 'error', message: 'Selecciona el sexo del vendedor.' })
            return
        }
        try {
            setLoading(true)
            setMessage(null)
            const request = toRequest(state, state.sexo)
            const response: WrapperResponse<VendedorDTO> = state.id
                ? await (await APIInvoke.invokePUT(`/vendedores/${vendedorId || state.id}`, request)).json()
                : await (await APIInvoke.invokePOST('/vendedores', request)).json()

            if (response.ok && response.body) {
                const saved = response.body
                setState((current) => ({ ...current, id: saved.id }))
                setMessage({ type: 'success', message: 'Vendedor guardado correctamente' })
                if (onCompleted) onCompleted(saved)
                else history.push({ pathname: SELLERS_PATH, state: { detailId: saved.id, backUrl: SELLERS_PATH } })
            } else {
                setMessage({ type: 'error', message: response.message || SAVE_ERROR })
            }
        } catch (error) {
            console.error('error al guardar el vendedor', error)
            setMessage({ type: 'error', message: SAVE_ERROR })
        } finally {
            setLoading(false)
        }
    }

    const cancel = () => {
        if (modal) onCompleted?.()
        else history.push(backUrl)
    }

    return {
        form: state,
        loading,
        busyLabel: vendedorId && !state.id ? 'Cargando vendedor…' : 'Guardando vendedor…',
        message,
        title: state.id ? 'Editar vendedor' : 'Nuevo vendedor',
        isEdit: Boolean(state.id),
        backTo: modal ? undefined : backUrl,
        change,
        save,
        cancel,
    }
}
